import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import { printBill, downloadBill } from '../../utils/pdf';
import toast from 'react-hot-toast';

export default function AdminBills() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (id) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await API.delete(`/bills/${id}`);
      toast.success('Bill deleted and stock restored successfully!');
      const r = await API.get('/bills');
      setBills(r.data);
      if (selected?.id === id) {
        setSelected(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    API.get('/bills').then(r => setBills(r.data)).catch(() => toast.error('Failed to load bills')).finally(() => setLoading(false));
  }, []);

  const filtered = bills.filter(b => b.invoiceNo?.includes(search) || b.customer?.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout title="All Bills">
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 400px' : '1fr', gap: 24 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Bills ({filtered.length})</span>
            <div className="search-bar" style={{ width: 240 }}>
              <span className="search-icon">🔍</span>
              <input className="form-control" placeholder="Search invoice or customer..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 36 }} />
            </div>
          </div>
          <div className="table-wrap">
            {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div> :
              filtered.length === 0 ? <div className="empty-state"><div className="empty-icon">🧾</div><div className="empty-title">No bills found</div></div> : (
                <table>
                  <thead><tr><th>Invoice No</th><th>Customer</th><th>Items</th><th>Total</th><th>Mode</th><th>Date</th><th>Employee</th><th></th></tr></thead>
                  <tbody>
                    {filtered.map(b => (
                      <tr key={b.id} style={{ cursor: 'pointer', background: selected?.id === b.id ? 'var(--gold-pale)' : '' }}>
                        <td onClick={() => setSelected(b)}><strong style={{ color: 'var(--navy)' }}>{b.invoiceNo}</strong></td>
                        <td onClick={() => setSelected(b)}>{b.customer?.name}</td>
                        <td onClick={() => setSelected(b)}>{b.items?.length} item{b.items?.length !== 1 ? 's' : ''}</td>
                        <td onClick={() => setSelected(b)}><strong style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>₹{b.total?.toFixed(2)}</strong></td>
                        <td onClick={() => setSelected(b)}><span className={`badge ${b.paymentMode === 'cash' ? 'badge-green' : b.paymentMode === 'card' ? 'badge-navy' : b.paymentMode === 'credit' ? 'badge-red' : b.paymentMode === 'discount' ? 'badge-amber' : 'badge-amber'}`}>{b.paymentMode}</span></td>
                        <td onClick={() => setSelected(b)}>{new Date(b.createdAt).toLocaleDateString('en-IN')}</td>
                        <td onClick={() => setSelected(b)}>{b.employee?.name || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-outline btn-sm" onClick={e => { e.stopPropagation(); downloadBill(b); }} title="Download PDF">⬇️</button>
                            <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); printBill(b); }} title="Print">🖨️</button>
                            <button className="btn btn-success btn-sm" onClick={e => { e.stopPropagation(); navigate(`/billing?edit=${b.id}`); }} title="Edit Invoice">✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); setConfirmDelete(b); }} title="Delete Invoice">🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>

        {selected && (
          <div className="card fade-in">
            <div className="card-header">
              <div><div className="card-title">{selected.invoiceNo}</div><div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 2 }}>{new Date(selected.createdAt).toLocaleString('en-IN')}</div></div>
              <button className="modal-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="card-body">
              <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-100)' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Customer</div>
                <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{selected.customer?.name}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{selected.customer?.phone}</div>
              </div>
              <table style={{ marginBottom: 12 }}>
                <thead><tr><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
                <tbody>
                  {(selected.items || []).map(item => {
                    const retQty = item.returnQty || item.return_qty || item.returnqty || 0;
                    const priceVal = item.price || item.unitRate || 0;
                    return (
                      <React.Fragment key={item.id}>
                        <tr>
                          <td>{item.productName}</td>
                          <td>{item.quantity}</td>
                          <td>₹{priceVal.toFixed(2)}</td>
                          <td><strong>₹{(item.quantity * priceVal).toFixed(2)}</strong></td>
                        </tr>
                        {retQty > 0 && (
                          <tr style={{ background: 'rgba(239, 68, 68, 0.05)', color: 'var(--red)' }}>
                            <td><span style={{ fontWeight: 'bold' }}>[RETURN]</span> {item.productName}</td>
                            <td>{retQty}</td>
                            <td>-₹{priceVal.toFixed(2)}</td>
                            <td><strong>-₹{(retQty * priceVal).toFixed(2)}</strong></td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ borderTop: '2px solid var(--gold)', paddingTop: 12, textAlign: 'right' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>Subtotal: ₹{selected.subtotal?.toFixed(2)}</div>
                {selected.taxPercent > 0 && <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>Tax ({selected.taxPercent}%): ₹{selected.tax?.toFixed(2)}</div>}
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--navy)', fontWeight: 700, marginTop: 4 }}>Total: ₹{selected.total?.toFixed(2)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => downloadBill(selected)}>⬇️ Download PDF</button>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => printBill(selected)}>🖨️ Print</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">⚠️</div>
            <div className="confirm-title">Delete Bill?</div>
            <div className="confirm-desc">
              Are you sure you want to permanently delete bill <strong>{confirmDelete.invoiceNo}</strong>?
              This will restore all items back to product stock and delete the associated payments.
            </div>
            <div className="confirm-buttons">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => {
                const id = confirmDelete.id;
                setConfirmDelete(null);
                handleDelete(id);
              }} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
